<template>
    <v-card outlined class="mb-4">
        <v-card-title class="text-subtitle-1 py-2">
            <v-icon left small color="green darken-2">mdi-flag-checkered</v-icon>
            Planejamento de Oferta
            <v-spacer />
            <v-chip v-if="totalIntents > 0" small color="green lighten-4" class="ml-2">
                {{ totalIntents }} disciplina(s) planejada(s)
            </v-chip>
            <v-chip v-if="draftMode && totalIntents > 0" small color="amber lighten-4" class="ml-2">
                <v-icon x-small left>mdi-content-save-edit-outline</v-icon>
                Rascunho — será salvo com a turma
            </v-chip>
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn small text color="purple darken-1" class="ml-2" v-bind="attrs" v-on="on">
                        <v-icon left small>mdi-cursor-default-click</v-icon>
                        Adicionar disciplina
                        <v-icon right small>mdi-chevron-down</v-icon>
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item
                        :disabled="!matrizDisciplines.length"
                        @click="openMatrizPicker"
                    >
                        <v-list-item-icon class="mr-2">
                            <v-icon small>mdi-layers-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Da matriz da turma</v-list-item-title>
                            <v-list-item-subtitle v-if="!matrizDisciplines.length">
                                Selecione uma matriz acima
                            </v-list-item-subtitle>
                            <v-list-item-subtitle v-else>
                                {{ matrizDisciplines.length }} disciplina(s) disponíveis
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="findDisciplineDialog = true">
                        <v-list-item-icon class="mr-2">
                            <v-icon small>mdi-magnify</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Buscar em todas</v-list-item-title>
                            <v-list-item-subtitle>Universo completo de disciplinas</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn v-if="groupId" icon small @click="load" :loading="loading" class="ml-2" title="Atualizar">
                <v-icon small>mdi-refresh</v-icon>
            </v-btn>
        </v-card-title>

        <v-divider />

        <!-- Seleção do módulo da matriz da turma (mesmo campo de model.curriculum_sequency_item_id) -->
        <div class="pa-3 grey lighten-5">
            <v-select
                v-if="showSelect"
                :value="localSequencyId"
                @change="onSequencyChange"
                :items="modules || []"
                item-text="name"
                item-value="id"
                label="Módulo da Matriz"
                :disabled="!modules || !modules.length || adding"
                :loading="adding"
                :hint="moduleHint"
                persistent-hint
                dense
                outlined
                hide-details="auto"
                clearable
            />
            <v-alert v-if="addMessage" :type="addMessageType" text dense class="mt-2 mb-0 caption">
                {{ addMessage }}
            </v-alert>
        </div>

        <v-divider />

        <v-card-text class="py-2">
            <div v-if="loading && groups.length === 0" class="text-center grey--text py-4">
                <v-progress-circular indeterminate size="20" width="2" />
                <span class="ml-2 caption">Carregando planejamento…</span>
            </div>

            <v-alert v-if="offerErrors.length" type="error" text dense class="mb-3">
                <div class="font-weight-medium">A turma não foi criada. Ajuste o planejamento e salve novamente.</div>
                <ul class="ma-0 mt-1 pl-4 caption">
                    <li v-for="(err, i) in offerErrors" :key="i">{{ err }}</li>
                </ul>
            </v-alert>

            <div v-if="draftMode && !draftIntents.length" class="grey--text caption text-center py-3">
                Monte o planejamento agora. As disciplinas serão gravadas junto com a turma.
            </div>

            <!-- Rascunho: só existe em memória até a turma ser salva, então não há
                 pré-requisito avaliado, grid gerado nem toggle de oferta. -->
            <div v-else-if="draftMode">
                <v-simple-table dense>
                    <thead>
                        <tr>
                            <th>Disciplina</th>
                            <th width="100">Código</th>
                            <th width="80" class="text-right">CH</th>
                            <th width="120">Origem</th>
                            <th width="60" class="text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in draftIntents" :key="item.discipline_id">
                            <td>{{ item.name || '—' }}</td>
                            <td class="caption grey--text">{{ item.code }}</td>
                            <td class="text-right caption">{{ formatWorkLoad(item) }}</td>
                            <td>
                                <v-chip
                                    x-small
                                    :color="item.origin === 'module' ? 'blue lighten-4' : 'purple lighten-4'"
                                >
                                    <v-icon x-small left>{{ item.origin === 'module' ? 'mdi-layers-triple' : 'mdi-cursor-default-click' }}</v-icon>
                                    {{ item.origin === 'module' ? 'Módulo' : 'Manual' }}
                                </v-chip>
                            </td>
                            <td class="text-right">
                                <v-btn icon x-small @click="removeDraftIntent(item.discipline_id)" title="Remover do rascunho">
                                    <v-icon small color="red darken-1">mdi-close</v-icon>
                                </v-btn>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </div>

            <div v-else-if="groups.length === 0" class="grey--text caption text-center py-3">
                Nenhuma disciplina no planejamento.
                Use o formulário acima para adicionar disciplinas de um módulo,
                ou marque pelo
                <router-link to="/percurso-formativo" target="_blank">Percurso Formativo</router-link>.
            </div>

            <div v-else>
                <div
                    v-for="(grp, idx) in groups"
                    :key="grp.work_time.id"
                    :class="idx > 0 ? 'mt-4' : ''"
                >
                    <div class="d-flex align-center mb-2">
                        <v-icon left small color="grey darken-1">mdi-calendar</v-icon>
                        <strong>{{ workTimeLabel(grp.work_time) }}</strong>
                        <v-chip x-small color="green lighten-5" class="ml-2">
                            {{ grp.count }} planejada(s)
                        </v-chip>
                        <v-chip v-if="grp.pending_count != null && grp.pending_count < grp.count" x-small color="grey lighten-2" class="ml-1">
                            {{ grp.count - grp.pending_count }} já gerada(s)
                        </v-chip>
                        <v-tooltip v-if="hasBlocking(grp)" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip x-small color="amber lighten-4" class="ml-2" v-bind="attrs" v-on="on">
                                    <v-icon x-small left>mdi-alert</v-icon>
                                    {{ countBlocking(grp) }} com pré-req. pendente
                                </v-chip>
                            </template>
                            <span>Disciplinas com pré-requisito não atendido. Resolva antes de gerar grids.</span>
                        </v-tooltip>
                        <v-spacer />
                        <v-btn
                            v-if="(grp.pending_count == null ? grp.count : grp.pending_count) > 0"
                            small
                            color="orange darken-1"
                            text
                            @click="$emit('bulk-generate', pendingGroupSubset(grp))"
                            title="Gerar oferta para as disciplinas ainda pendentes"
                        >
                            <v-icon left small>mdi-flash</v-icon>
                            Gerar Oferta
                        </v-btn>
                    </div>

                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th width="64">Ofertar</th>
                                <th>Disciplina</th>
                                <th width="100">Código</th>
                                <th width="80" class="text-right">CH</th>
                                <th width="120">Origem</th>
                                <th width="140">Status</th>
                                <th width="60" class="text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="intent in grp.intents"
                                :key="intent.id"
                                :class="[
                                    intent.has_grid ? 'grid-generated-row' : '',
                                    intent.disabled ? 'disabled-row' : '',
                                ]"
                            >
                                <td>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <div v-bind="attrs" v-on="on" style="display:inline-block">
                                                <v-switch
                                                    :input-value="!intent.disabled"
                                                    :disabled="intent.has_grid || togglingId === intent.id"
                                                    :loading="togglingId === intent.id"
                                                    color="success"
                                                    inset
                                                    dense
                                                    hide-details
                                                    class="ma-0 pa-0"
                                                    @change="toggleEnabled(intent, $event, grp.work_time.id)"
                                                />
                                            </div>
                                        </template>
                                        <span v-if="intent.has_grid">Grid já gerado — não pode ser desligado por aqui</span>
                                        <span v-else-if="intent.disabled">Desligada manualmente. Religar inclui no planejamento.</span>
                                        <span v-else>Ligada — será ofertada. Desligar marca como decisão manual de não ofertar.</span>
                                    </v-tooltip>
                                </td>
                                <td>
                                    {{ intent.discipline ? intent.discipline.name : '—' }}
                                    <v-tooltip v-if="intent.has_join" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                icon
                                                x-small
                                                v-bind="attrs"
                                                v-on="on"
                                                @click.stop="openJoinDialog(intent)"
                                                class="ml-1"
                                            >
                                                <v-icon small>mdi-vector-link</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver detalhes da junção</span>
                                    </v-tooltip>
                                </td>
                                <td class="caption grey--text">{{ intent.discipline ? intent.discipline.code : '' }}</td>
                                <td class="text-right caption">{{ formatWorkLoad(intent.discipline) }}</td>
                                <td>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip
                                                x-small
                                                :color="intent.origin === 'module' ? 'blue lighten-4' : 'purple lighten-4'"
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                <v-icon x-small left>{{ intent.origin === 'module' ? 'mdi-layers-triple' : 'mdi-cursor-default-click' }}</v-icon>
                                                {{ intent.origin === 'module' ? 'Módulo' : 'Manual' }}
                                            </v-chip>
                                        </template>
                                        <span v-if="intent.origin === 'module'">Adicionada em lote via seleção de módulo no cadastro de turma.</span>
                                        <span v-else>Marcada individualmente pelo Percurso Formativo.</span>
                                    </v-tooltip>
                                </td>
                                <td>
                                    <v-chip v-if="intent.disabled" x-small color="grey lighten-2">
                                        <v-icon x-small left>mdi-power-plug-off</v-icon>
                                        Não ofertada
                                    </v-chip>
                                    <v-chip v-else-if="intent.has_grid" x-small color="green lighten-4">
                                        <v-icon x-small left>mdi-check-circle</v-icon>
                                        Disciplinas das turmas
                                    </v-chip>
                                    <v-chip v-else-if="intent.prerequisite_ok" x-small color="green lighten-4">
                                        <v-icon x-small left>mdi-check</v-icon>
                                        Pré-req OK
                                    </v-chip>
                                    <v-tooltip v-else-if="!intent.has_grid && !intent.prerequisite_ok" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip x-small color="amber lighten-4" v-bind="attrs" v-on="on">
                                                <v-icon x-small left>mdi-alert</v-icon>
                                                Aguardando pré-req
                                            </v-chip>
                                        </template>
                                        <div>
                                            <strong>Pré-requisito(s) sem oferta anterior:</strong>
                                            <ul class="ma-0 pl-3">
                                                <li v-for="p in intent.missing_prerequisites" :key="p.id">{{ p.name }}</li>
                                            </ul>
                                        </div>
                                    </v-tooltip>
                                </td>
                                <td class="text-right">
                                    <v-tooltip v-if="intent.has_grid" bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                icon
                                                x-small
                                                v-bind="attrs"
                                                v-on="on"
                                                :loading="clearingIntentId === intent.id"
                                                @click="confirmClearOffer(intent, grp.work_time.id)"
                                            >
                                                <v-icon small color="orange darken-1">mdi-eraser</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Limpar oferta (excluir disciplinas das turmas)</span>
                                    </v-tooltip>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </div>
            </div>
        </v-card-text>

        <v-dialog v-model="clearDialog" max-width="500">
            <v-card>
                <v-card-title class="text-subtitle-1">Limpar oferta da disciplina?</v-card-title>
                <v-card-text>
                    <p>
                        Esta ação <strong>exclui as disciplinas das turmas</strong> de
                        <strong>{{ clearTarget && clearTarget.discipline ? clearTarget.discipline.name : '' }}</strong>
                        no período letivo selecionado.
                    </p>
                    <v-alert
                        v-if="clearTarget && clearTarget.has_join"
                        type="info"
                        text
                        dense
                        class="mb-2 caption"
                    >
                        <v-icon left small>mdi-vector-link</v-icon>
                        Esta disciplina possui junção. As disciplinas das turmas serão
                        <strong>desvinculadas da junção</strong> (a junção em si e as outras
                        disciplinas vinculadas permanecem).
                    </v-alert>
                    <p class="caption grey--text mb-0">
                        Disciplinas das turmas com professor atribuído não serão excluídos (você precisará remover a atribuição antes).
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="clearDialog = false">Cancelar</v-btn>
                    <v-btn color="red darken-1" text @click="doClearOffer" :loading="clearing">Limpar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <sys-find-discipline
            showSelect
            :singleSelect="false"
            :show.sync="findDisciplineDialog"
            @onSelect="onDisciplinesPicked"
        />

        <join-info-dialog
            v-if="groupId"
            :show.sync="joinDialog"
            :group-id="groupId"
            :discipline="joinDialogDiscipline"
        />

        <v-dialog v-model="matrizPickerDialog" max-width="640" scrollable>
            <v-card>
                <v-card-title class="text-subtitle-1">
                    <v-icon left small>mdi-layers-outline</v-icon>
                    Adicionar disciplinas da matriz
                    <v-spacer />
                    <v-btn icon small @click="matrizPickerDialog = false"><v-icon small>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text style="max-height: 60vh;" class="pa-0">
                    <div v-if="!matrizDisciplines.length" class="grey--text caption text-center py-6">
                        Nenhuma disciplina na matriz.
                    </div>
                    <v-list v-else dense subheader>
                        <template v-for="mod in modulesForPicker">
                            <v-subheader :key="`h-${mod.id}`" class="grey lighten-4">{{ mod.name }}</v-subheader>
                            <v-list-item
                                v-for="sd in (mod.sequencyDisciplines || [])"
                                :key="`${mod.id}-${sd.discipline && sd.discipline.id}`"
                                :disabled="!sd.discipline || isMatrizItemDisabled(sd.discipline)"
                                @click="toggleMatrizPick(sd.discipline)"
                            >
                                <v-list-item-action class="mr-2">
                                    <v-simple-checkbox
                                        :value="isMatrizItemSelectedOrLocked(sd.discipline)"
                                        :disabled="!sd.discipline || isMatrizItemDisabled(sd.discipline)"
                                        @input="toggleMatrizPick(sd.discipline)"
                                    />
                                </v-list-item-action>
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ sd.discipline ? sd.discipline.name : '—' }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle v-if="sd.discipline && sd.discipline.code" class="caption">
                                        {{ sd.discipline.code }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="sd.discipline && isMatrizItemManualCursada(sd.discipline.id)">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip small color="amber lighten-4" v-bind="attrs" v-on="on">
                                                <v-icon x-small left>mdi-check-bold</v-icon>
                                                {{ manualCompletionLabel(sd.discipline.id) || 'Já cursada' }}
                                            </v-chip>
                                        </template>
                                        <div>
                                            <strong>Marcada manualmente como cursada</strong>
                                            <div v-if="manualCompletions[sd.discipline.id] && manualCompletions[sd.discipline.id].notes" class="mt-1">
                                                <em>"{{ manualCompletions[sd.discipline.id].notes }}"</em>
                                            </div>
                                        </div>
                                    </v-tooltip>
                                </v-list-item-action>
                                <v-list-item-action v-else-if="sd.discipline && isMatrizItemAlreadyOffered(sd.discipline.id)">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-chip small color="green lighten-4" v-bind="attrs" v-on="on">
                                                <v-icon x-small left>mdi-check-circle</v-icon>
                                                {{ workTimeLabelsFor(sd.discipline.id) || 'Já ofertada' }}
                                            </v-chip>
                                        </template>
                                        <div>
                                            <strong>Ofertada nos períodos:</strong>
                                            <ul v-if="(trailOfferedWorkTimes[sd.discipline.id] || []).length" class="ma-0 pl-3">
                                                <li v-for="wt in trailOfferedWorkTimes[sd.discipline.id]" :key="wt.id">
                                                    {{ wt.name || (wt.year + '/' + wt.period) }}
                                                </li>
                                            </ul>
                                            <div v-else class="caption">Sem detalhes disponíveis.</div>
                                        </div>
                                    </v-tooltip>
                                </v-list-item-action>
                                <v-list-item-action v-else-if="sd.discipline && isAlreadyInPlanning(sd.discipline.id)">
                                    <v-chip x-small color="green lighten-5">Já no planejamento</v-chip>
                                </v-list-item-action>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-card-text>
                <v-divider />
                <v-card-actions>
                    <span class="caption grey--text ml-2">{{ matrizPicked.length }} selecionada(s)</span>
                    <v-spacer />
                    <v-btn text @click="matrizPickerDialog = false">Cancelar</v-btn>
                    <v-btn
                        color="purple darken-1"
                        depressed
                        :disabled="!matrizPicked.length"
                        :loading="adding"
                        @click="confirmMatrizPick"
                    >
                        Adicionar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import JoinInfoDialog from '../../../domains/learning-path/components/JoinInfoDialog.vue'

export default {
    name: 'OfferIntentsPlanning',

    components: { JoinInfoDialog },

    props: {
        groupId: { type: [Number, String], default: null },
        groupWorkTimeId: { type: [Number, String], default: null },
        modules: { type: Array, default: () => [] },
        currentSequencyId: { type: [Number, String], default: null },
        // Erros de oferta devolvidos pelo backend ao salvar a turma (turma nova).
        offerErrors: { type: Array, default: () => [] },
    },

    data: () => ({
        loading: false,
        groups: [],
        // Planejamento montado antes de a turma existir. Vai no payload do save.
        draftIntents: [],
        trailOfferedSet: new Set(),
        trailOfferedWorkTimes: {},
        manualCompletions: {},
        adding: false,
        addMessage: '',
        addMessageType: 'success',
        findDisciplineDialog: false,
        matrizPickerDialog: false,
        matrizPicked: [],
        togglingId: null,
        localSequencyId: null,
        showSelect: true,
        joinDialog: false,
        joinDialogDiscipline: null,
        clearDialog: false,
        clearTarget: null,
        clearWorkTimeId: null,
        clearing: false,
        clearingIntentId: null,
    }),

    computed: {
        // Sem turma salva o card opera em memória — nenhum request sai daqui.
        draftMode() {
            return !this.groupId
        },
        totalIntents() {
            if (this.draftMode) return this.draftIntents.length
            return this.groups.reduce((sum, g) => sum + (g.count || 0), 0)
        },
        moduleHint() {
            if (!this.modules || !this.modules.length) return 'Selecione uma matriz acima'
            return 'Ao escolher o módulo, suas disciplinas são adicionadas automaticamente ao planejamento.'
        },
        // Achata as disciplinas da matriz em uma lista única (com referência ao módulo).
        // Mostra TODAS, incluindo as já ofertadas — o picker apenas marca as
        // já ofertadas/no planejamento como desabilitadas.
        matrizDisciplines() {
            const out = []
            for (const mod of (this.modules || [])) {
                for (const sd of (mod.sequencyDisciplines || [])) {
                    if (sd.discipline && sd.discipline.id) out.push(sd.discipline)
                }
            }
            return out
        },
        // Módulos completos — usado no picker. Sem filtro de "já ofertadas".
        modulesForPicker() {
            return (this.modules || []).filter(mod =>
                (mod.sequencyDisciplines || []).length > 0
            )
        },
        planningDisciplineIdSet() {
            // Considera "já no planejamento" só as intents pendentes (sem grid).
            // As com grid já são tratadas pelo trailOfferedSet (filtragem do picker).
            const set = new Set()
            if (this.draftMode) {
                for (const d of this.draftIntents) set.add(d.discipline_id)
                return set
            }
            for (const g of this.groups) {
                for (const it of (g.intents || [])) {
                    if (it.discipline && it.discipline.id && !it.has_grid) set.add(it.discipline.id)
                }
            }
            return set
        },
    },

    watch: {
        groupId: {
            immediate: true,
            handler(v) {
                if (!v) return
                // Turma passou a existir: o rascunho já foi persistido no save.
                this.draftIntents = []
                this.load()
            }
        },
        currentSequencyId: {
            immediate: true,
            handler(v) { this.localSequencyId = v || null }
        },
    },

    methods: {
        ...mapActions('domainLearningPath', ['ActionListOfferIntents', 'ActionAddOfferIntent', 'ActionSetEnabledOfferIntent', 'ActionClearOfferIntent']),

        // Hidrata um id de disciplina com os dados que já vieram na matriz.
        disciplineFromMatriz(id) {
            return this.matrizDisciplines.find(d => d && d.id === id) || null
        },

        addDraftIntents(items, origin) {
            const list = Array.isArray(items) ? items : []
            list.forEach(d => {
                if (!d || !d.id) return
                if (this.draftIntents.some(x => x.discipline_id === d.id)) return
                this.draftIntents.push({
                    discipline_id: d.id,
                    name: d.name || d.name_complete || '',
                    code: d.code || '',
                    work_load: d.work_load,
                    origin: origin,
                })
            })
            this.emitDraft()
        },

        removeDraftIntent(disciplineId) {
            this.draftIntents = this.draftIntents.filter(d => d.discipline_id !== disciplineId)
            this.emitDraft()
        },

        emitDraft() {
            this.$emit('update:draftIntents', this.draftIntents.slice())
        },

        confirmClearOffer(intent, workTimeId) {
            if (this.draftMode) return
            if (!intent || !intent.discipline) return
            this.clearTarget = intent
            this.clearWorkTimeId = workTimeId
            this.clearDialog = true
        },

        async doClearOffer() {
            if (this.draftMode) return
            if (!this.clearTarget || !this.clearTarget.discipline) return
            this.clearing = true
            this.clearingIntentId = this.clearTarget.id
            try {
                await this.ActionClearOfferIntent({
                    id: this.groupId,
                    discipline_id: this.clearTarget.discipline.id,
                    work_time_id: this.clearWorkTimeId,
                })
                this.addMessageType = 'success'
                this.addMessage = 'Oferta da disciplina removida.'
                this.clearDialog = false
                this.clearTarget = null
                this.clearWorkTimeId = null
                await this.load()
            } catch (e) {
                this.addMessageType = 'error'
                let msg = null
                if (e && typeof e.message === 'string' && e.message.length) msg = e.message
                else if (e && e.body && e.body.message) msg = e.body.message
                this.addMessage = msg || 'Não foi possível limpar a oferta.'
            } finally {
                this.clearing = false
                this.clearingIntentId = null
            }
        },

        openJoinDialog(intent) {
            if (!intent || !intent.discipline) return
            this.joinDialogDiscipline = intent.discipline
            this.joinDialog = true
        },

        async toggleEnabled(intent, newValue, workTimeId) {
            if (this.draftMode) return
            if (!intent || !intent.discipline) return
            if (intent.has_grid) return // segurança extra
            this.togglingId = intent.id
            // Atualização otimista
            const oldDisabled = intent.disabled
            intent.disabled = !newValue
            try {
                await this.ActionSetEnabledOfferIntent({
                    id: this.groupId,
                    discipline_id: intent.discipline.id,
                    work_time_id: workTimeId,
                    enabled: !!newValue,
                })
                // Refresh pra recomputar contadores / pendências
                await this.load()
            } catch (e) {
                intent.disabled = oldDisabled // rollback
                this.addMessageType = 'error'
                this.addMessage = 'Não foi possível alternar o estado da disciplina.'
            } finally {
                this.togglingId = null
            }
        },

        formatWorkLoad(discipline) {
            if (!discipline) return ''
            const wl = discipline.work_load
            if (wl === null || wl === undefined || wl === '') return '—'
            const n = Number(wl)
            if (isNaN(n)) return '—'
            return (Number.isInteger(n) ? n : n.toFixed(1)) + 'h'
        },

        workTimeLabel(wt) {
            if (!wt) return ''
            if (wt.name) return wt.name
            if (wt.year && wt.period) return wt.year + '/' + wt.period
            return '#' + wt.id
        },

        hasBlocking(grp) {
            return (grp.intents || []).some(i => !i.has_grid && !i.prerequisite_ok)
        },

        countBlocking(grp) {
            return (grp.intents || []).filter(i => !i.has_grid && !i.prerequisite_ok).length
        },

        // Retorna o grp filtrado pra incluir TODAS as intents ATIVAS (não-disabled),
        // incluindo as que já têm grid. Isso é importante porque o backend
        // (generateGridsData) DELETA grids que não estejam no payload — passar só
        // as pendentes faria as grids existentes serem apagadas. Backend ignora
        // automaticamente as que já têm chave igual.
        pendingGroupSubset(grp) {
            return {
                ...grp,
                intents: (grp.intents || []).filter(i => !i.disabled),
            }
        },

        async load() {
            if (!this.groupId) return
            this.loading = true
            try {
                const res = await this.ActionListOfferIntents({ id: this.groupId })
                // Compat: aceita resposta antiga (array) e nova (objeto)
                let data = res && res.data !== undefined ? res.data : res
                if (Array.isArray(data)) {
                    this.groups = data
                    this.trailOfferedSet = new Set()
                    this.trailOfferedWorkTimes = {}
                    this.manualCompletions = {}
                } else if (data && typeof data === 'object') {
                    this.groups = Array.isArray(data.groups) ? data.groups : []
                    const ids = Array.isArray(data.trail_offered_discipline_ids) ? data.trail_offered_discipline_ids : []
                    this.trailOfferedSet = new Set(ids.map(id => Number(id)))
                    this.trailOfferedWorkTimes = data.trail_offered_work_times || {}
                    this.manualCompletions = data.manual_completions || {}
                } else {
                    this.groups = []
                    this.trailOfferedSet = new Set()
                    this.trailOfferedWorkTimes = {}
                    this.manualCompletions = {}
                }
            } catch (e) {
                this.groups = []
                this.trailOfferedSet = new Set()
                this.trailOfferedWorkTimes = {}
                this.manualCompletions = {}
            } finally {
                this.loading = false
            }
        },

        isAlreadyInPlanning(disciplineId) {
            return disciplineId && this.planningDisciplineIdSet.has(disciplineId)
        },

        // Disciplina já tem grid em alguma turma da trilha (qualquer wt).
        isMatrizItemAlreadyOffered(disciplineId) {
            return !!(disciplineId && this.trailOfferedSet.has(Number(disciplineId)))
        },

        // Resume os work_times de uma disciplina ofertada em "2025/1, 2025/2 (+2)" estilo.
        // Mostra até 2 períodos inline; o restante fica no tooltip.
        workTimeLabelsFor(disciplineId) {
            const wts = this.trailOfferedWorkTimes[disciplineId] || []
            if (!wts.length) return ''
            const fmt = wt => wt.name || ((wt.year || '') + '/' + (wt.period || ''))
            if (wts.length <= 2) return wts.map(fmt).join(', ')
            return wts.slice(0, 2).map(fmt).join(', ') + ' (+' + (wts.length - 2) + ')'
        },

        // Disciplina foi marcada manualmente como cursada (em qualquer turma da trilha).
        isMatrizItemManualCursada(disciplineId) {
            return !!(disciplineId && this.manualCompletions && this.manualCompletions[disciplineId])
        },

        // Formata "2023/1" a partir do registro manual (ano + período).
        manualCompletionLabel(disciplineId) {
            const m = this.manualCompletions && this.manualCompletions[disciplineId]
            if (!m) return ''
            if (m.year && m.period_number) return m.year + '/' + m.period_number
            return m.year ? String(m.year) : ''
        },

        // Desabilita o item se já está ofertada, no planejamento ou cursada manualmente.
        isMatrizItemDisabled(discipline) {
            if (!discipline || !discipline.id) return true
            return this.isMatrizItemAlreadyOffered(discipline.id)
                || this.isAlreadyInPlanning(discipline.id)
                || this.isMatrizItemManualCursada(discipline.id)
        },

        // Estado visual do checkbox: marcado se selecionado pelo usuário,
        // OU se está "travado" como já planejado/ofertado/cursada.
        isMatrizItemSelectedOrLocked(discipline) {
            if (!discipline || !discipline.id) return false
            return this.matrizPicked.indexOf(discipline.id) >= 0
                || this.isAlreadyInPlanning(discipline.id)
                || this.isMatrizItemAlreadyOffered(discipline.id)
                || this.isMatrizItemManualCursada(discipline.id)
        },

        openMatrizPicker() {
            this.matrizPicked = []
            this.matrizPickerDialog = true
        },

        toggleMatrizPick(discipline) {
            if (!discipline || !discipline.id) return
            if (this.isAlreadyInPlanning(discipline.id)) return
            const idx = this.matrizPicked.indexOf(discipline.id)
            if (idx >= 0) this.matrizPicked.splice(idx, 1)
            else this.matrizPicked.push(discipline.id)
        },

        async confirmMatrizPick() {
            if (!this.matrizPicked.length) return
            const ids = this.matrizPicked.slice()
            this.matrizPickerDialog = false
            // Reaproveita o mesmo fluxo do sys-find-discipline
            await this.onDisciplinesPicked(ids.map(id => ({ id })))
        },

        async onDisciplinesPicked(items) {
            const list = Array.isArray(items) ? items : (items ? [items] : [])
            const ids = list.map(d => d && (d.id || d.discipline_id)).filter(Boolean)
            if (!ids.length) return

            if (this.draftMode) {
                // O picker da matriz entrega só { id }; o sys-find-discipline
                // entrega o objeto completo. Hidrata pela matriz quando faltar.
                this.addDraftIntents(
                    list.map(d => (d && d.name ? d : this.disciplineFromMatriz(d.id) || d)),
                    'manual'
                )
                this.addMessageType = 'success'
                this.addMessage = ids.length + ' disciplina(s) adicionada(s) ao rascunho.'
                return
            }

            if (!this.groupWorkTimeId) {
                this.addMessageType = 'error'
                this.addMessage = 'Turma sem período letivo definido.'
                return
            }

            this.adding = true
            this.addMessage = ''
            try {
                // origin=manual, replace=false → adiciona sem afetar lote de módulo
                await this.ActionAddOfferIntent({
                    id: this.groupId,
                    work_time_id: this.groupWorkTimeId,
                    discipline_ids: ids,
                    origin: 'manual',
                })
                this.addMessageType = 'success'
                this.addMessage = ids.length + ' disciplina(s) adicionada(s) manualmente ao planejamento.'
                await this.load()
            } catch (e) {
                this.addMessageType = 'error'
                const errBody = e && e.body ? e.body : (e && e.data ? e.data : null)
                // Prefere a `message` (texto pronto pro usuário); cai pra primeira `errors`.
                let msg = null
                if (errBody && typeof errBody.message === 'string' && errBody.message.length) {
                    msg = errBody.message
                } else if (errBody && errBody.errors) {
                    const first = Object.values(errBody.errors)[0]
                    msg = Array.isArray(first) ? first[0] : String(first)
                }
                this.addMessage = msg || 'Não foi possível adicionar as disciplinas.'
            } finally {
                this.adding = false
            }
        },

        // Reverte o select pro valor anterior. Estratégia "tela queimada":
        //  1) Destrói o v-select (v-if=false)
        //  2) Em setTimeout(0), seta o valor local e emite pro pai
        //  3) Recria o v-select — lê :value fresh do data
        // Usa setTimeout em vez de nextTick pra garantir que o ciclo de render
        // do destroy completou antes de re-criar com o valor novo.
        revertSequency(previousSequencyId) {
            const v = previousSequencyId || null
            this.showSelect = false
            setTimeout(() => {
                this.localSequencyId = v
                this.$emit('update:currentSequencyId', v)
                this.$nextTick(() => { this.showSelect = true })
            }, 0)
        },

        disciplineIdsOfSequency(sequencyId) {
            if (!sequencyId || !this.modules) return []
            const mod = this.modules.find(m => m.id === sequencyId)
            if (!mod || !mod.sequencyDisciplines) return []
            return mod.sequencyDisciplines
                .map(sd => sd.discipline && sd.discipline.id)
                .filter(Boolean)
        },

        async onSequencyChange(sequencyId) {
            // Captura antes de emitir — currentSequencyId ainda reflete o estado do banco.
            const previousSequencyId = this.currentSequencyId
            // Propaga para o pai (sincroniza model.curriculum_sequency_item_id)
            this.$emit('update:currentSequencyId', sequencyId || null)

            if (this.draftMode) {
                const draftIds = sequencyId ? this.disciplineIdsOfSequency(sequencyId) : []
                this.localSequencyId = sequencyId || null
                // Módulo é excludente: trocar substitui só o lote de origem 'module'.
                this.draftIntents = this.draftIntents.filter(d => d.origin !== 'module')
                this.addDraftIntents(
                    draftIds.map(id => this.disciplineFromMatriz(id)).filter(Boolean),
                    'module'
                )
                this.addMessageType = 'success'
                this.addMessage = sequencyId
                    ? (draftIds.length + ' disciplina(s) carregada(s) do módulo para o rascunho.')
                    : 'Rascunho do módulo limpo.'
                return
            }

            if (!this.groupWorkTimeId) {
                this.addMessageType = 'error'
                this.addMessage = 'Turma sem período letivo definido.'
                this.revertSequency(previousSequencyId)
                return
            }

            const disciplineIds = sequencyId ? this.disciplineIdsOfSequency(sequencyId) : []

            this.adding = true
            this.addMessage = ''
            try {
                // replace=true → substitui o planejamento desse work_time pelo
                // conteúdo do módulo selecionado (ou limpa, se null).
                await this.ActionAddOfferIntent({
                    id: this.groupId,
                    work_time_id: this.groupWorkTimeId,
                    discipline_ids: disciplineIds,
                    replace: true,
                    origin: 'module',
                    // Persiste a escolha imediatamente no banco pra evitar
                    // divergência caso o usuário não clique "Atualizar" depois.
                    curriculum_sequency_item_id: sequencyId || null,
                })
                this.addMessageType = 'success'
                this.addMessage = sequencyId
                    ? (disciplineIds.length + ' disciplina(s) carregada(s) do módulo.')
                    : 'Planejamento limpo.'
                await this.load()
            } catch (e) {
                this.addMessageType = 'error'
                // Prefere `message` (texto pronto pro usuário); cai pra primeira `errors`.
                let msg = null
                if (e && typeof e.message === 'string' && e.message.length) {
                    msg = e.message
                } else if (e && e.errors) {
                    const first = Object.values(e.errors)[0]
                    msg = Array.isArray(first) ? first[0] : String(first)
                } else if (e && e.body) {
                    if (typeof e.body.message === 'string' && e.body.message.length) msg = e.body.message
                    else if (e.body.errors) {
                        const first = Object.values(e.body.errors)[0]
                        msg = Array.isArray(first) ? first[0] : String(first)
                    }
                }
                this.addMessage = msg || 'Não foi possível adicionar as disciplinas.'
                // ESSENCIAL: reverte o select pro módulo anterior
                this.revertSequency(previousSequencyId)
            } finally {
                this.adding = false
            }
        },
    },
}
</script>

<style scoped>
/* Linha esmaecida pra intents que já viraram grid (histórico). */
.grid-generated-row td {
    color: rgba(0, 0, 0, 0.5);
}
.grid-generated-row .caption {
    color: rgba(0, 0, 0, 0.4);
}
/* Linha "desligada" — strikethrough discreto. */
.disabled-row td {
    color: rgba(0, 0, 0, 0.45);
    text-decoration: line-through;
    text-decoration-color: rgba(0, 0, 0, 0.3);
}
.disabled-row td:first-child,
.disabled-row td:last-child {
    text-decoration: none;
}
</style>
